/**
 * Places API service
 * Handles all place/result-related API requests
 */

import { get, post, buildQueryString } from './api';
import type {
  Place,
  PlaceListItem,
  PlacesResponse,
  PlaceFilter,
  PlaceStats,
  PlaceWithReviews,
  ExportFormat,
  MapDataResponse,
  MapDataParams,
  SearchTermOption,
} from '@/types/place';
import type { ExportStatusResponse } from '@/types/api';

/**
 * API endpoints
 */
const ENDPOINTS = {
  PLACES: '/places',
  PLACES_MAP: '/places/map',
  PLACE: (id: string) => `/places/${id}`,
  PLACE_STATS: '/places/stats',
  JOB_RESULTS: (jobId: string) => `/jobs/${jobId}/places`,
  JOB_EXPORT: (jobId: string) => `/jobs/${jobId}/export`,
  EXPORT_STATUS: (exportId: string) => `/exports/${exportId}`,
};

/**
 * Backend response format for places list
 */
interface PlacesBackendResponse {
  data: PlaceListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Get paginated list of all places
 */
export async function getPlaces(filter?: PlaceFilter): Promise<PlacesResponse> {
  // Map frontend filter names to backend expected names
  const backendFilter: Record<string, unknown> = {};
  if (filter) {
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined) {
        // Map frontend filter names to backend expected names
        if (key === 'search') {
          backendFilter['searchQuery'] = value;
        } else if (key === 'sortBy') {
          backendFilter['orderBy'] = value;
        } else if (key === 'sortOrder') {
          backendFilter['orderDir'] = value;
        } else if (key === 'priceLevel' && Array.isArray(value)) {
          backendFilter['priceLevel'] = value.join(',');
        } else if (key === 'excludeClickedByUserIds' && Array.isArray(value)) {
          backendFilter['excludeClickedByUserIds'] = value.join(',');
        } else if (key === 'excludePlaceIds' && Array.isArray(value)) {
          backendFilter['excludePlaceIds'] = value.join(',');
        } else {
          backendFilter[key] = value;
        }
      }
    });
  }
  const queryString = Object.keys(backendFilter).length > 0 ? buildQueryString(backendFilter) : '';
  const response = await get<PlacesBackendResponse>(`${ENDPOINTS.PLACES}${queryString}`);

  // Transform backend response to PlacesResponse format
  const { data, pagination } = response;
  return {
    places: data || [],
    total: pagination?.total || 0,
    page: pagination?.offset ? Math.floor(pagination.offset / (pagination.limit || 20)) : 0,
    limit: pagination?.limit || 50,
    totalPages: pagination?.total ? Math.ceil(pagination.total / (pagination.limit || 50)) : 0,
  };
}

/**
 * Get optimized map data with server-side clustering
 *
 * At low zoom: Returns cluster centroids with counts (max 200)
 * At high zoom: Returns individual places (max 500)
 *
 * This dramatically reduces payload for world/continent views
 */
export async function getMapData(params: MapDataParams): Promise<MapDataResponse> {
  const queryString = buildQueryString(params as Record<string, unknown>);
  return get<MapDataResponse>(`${ENDPOINTS.PLACES_MAP}${queryString}`);
}

/**
 * Backend response format for job places
 */
interface JobPlacesBackendResponse {
  data: PlaceListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Get places for a specific job
 */
export async function getJobResults(
  jobId: string,
  filter?: PlaceFilter
): Promise<PlacesResponse> {
  // Apply the same filter name remapping as getPlaces()
  const backendFilter: Record<string, unknown> = {};
  if (filter) {
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'search') {
          backendFilter['searchQuery'] = value;
        } else if (key === 'sortBy') {
          backendFilter['orderBy'] = value;
        } else if (key === 'sortOrder') {
          backendFilter['orderDir'] = value;
        } else if (key === 'priceLevel' && Array.isArray(value)) {
          backendFilter['priceLevel'] = value.join(',');
        } else if (key === 'excludeClickedByUserIds' && Array.isArray(value)) {
          backendFilter['excludeClickedByUserIds'] = value.join(',');
        } else if (key === 'excludePlaceIds' && Array.isArray(value)) {
          backendFilter['excludePlaceIds'] = value.join(',');
        } else {
          backendFilter[key] = value;
        }
      }
    });
  }
  const queryString = Object.keys(backendFilter).length > 0 ? buildQueryString(backendFilter) : '';
  const response = await get<JobPlacesBackendResponse>(`${ENDPOINTS.JOB_RESULTS(jobId)}${queryString}`);

  // Transform backend response to PlacesResponse format
  const { data, pagination } = response;
  return {
    places: data || [],
    total: pagination?.total || 0,
    page: pagination?.offset ? Math.floor(pagination.offset / (pagination.limit || 20)) : 0,
    limit: pagination?.limit || 20,
    totalPages: pagination?.total ? Math.ceil(pagination.total / (pagination.limit || 20)) : 0,
  };
}

/**
 * Get a single place by ID
 */
export async function getPlace(placeId: string): Promise<Place> {
  return get<Place>(ENDPOINTS.PLACE(placeId));
}

/**
 * Get place with reviews
 */
export async function getPlaceWithReviews(placeId: string): Promise<PlaceWithReviews> {
  return get<PlaceWithReviews>(`${ENDPOINTS.PLACE(placeId)}?includeReviews=true`);
}

/**
 * Get place statistics
 */
export async function getPlaceStats(jobId?: string): Promise<PlaceStats> {
  const queryString = jobId ? `?jobId=${jobId}` : '';
  return get<PlaceStats>(`${ENDPOINTS.PLACE_STATS}${queryString}`);
}

/**
 * Search places across all jobs
 */
export async function searchPlaces(
  query: string,
  options?: {
    limit?: number;
    jobId?: string;
  }
): Promise<PlaceListItem[]> {
  const response = await getPlaces({
    search: query,
    limit: options?.limit || 50,
    jobId: options?.jobId,
  });
  return response.places;
}

/**
 * Backend response for stats (different from PlaceStats type)
 */
interface StatsBackendResponse {
  totalPlaces: number;
  avgRating: number;
  totalReviews: number;
  categories: Array<{ category: string; count: number }>;
  byJob: Array<{ jobId: string; count: number }>;
}

/**
 * Get unique categories from places (top 20 via stats endpoint)
 */
export async function getCategories(jobId?: string): Promise<string[]> {
  const queryString = jobId ? `?jobId=${jobId}` : '';
  const stats = await get<StatsBackendResponse>(`/places/stats${queryString}`);
  // Backend returns categories as array of {category, count} objects
  return stats.categories.map(c => c.category).sort();
}

/**
 * Get ALL distinct categories from places (no limit)
 * Uses the dedicated /places/categories endpoint
 */
export async function getAllCategories(): Promise<string[]> {
  const response = await get<{ categories: string[] }>('/places/categories');
  return response.categories;
}

/**
 * Response format for search terms endpoint
 */
interface SearchTermsResponse {
  searchTerms: SearchTermOption[];
}

/**
 * Get search terms (original search queries) that have found places
 * Returns array of {searchQuery, placeCount, remainingCount} sorted by place count descending
 */
export async function getSearchTerms(params?: {
  excludeClickedByUserIds?: string[];
  curatedCategoriesOnly?: boolean;
  nameBlacklistActive?: boolean;
}): Promise<SearchTermOption[]> {
  const queryParams: Record<string, unknown> = {};
  if (params?.excludeClickedByUserIds && params.excludeClickedByUserIds.length > 0) {
    queryParams.excludeClickedByUserIds = params.excludeClickedByUserIds.join(',');
  }
  if (params?.curatedCategoriesOnly) {
    queryParams.curatedCategoriesOnly = 'true';
  }
  if (params?.nameBlacklistActive) {
    queryParams.nameBlacklistActive = 'true';
  }
  const queryString = Object.keys(queryParams).length > 0 ? buildQueryString(queryParams) : '';
  const response = await get<SearchTermsResponse>(`/places/search-terms${queryString}`);
  return response.searchTerms || [];
}

/**
 * Country option with place count for filter dropdown
 */
export interface CountryOption {
  country: string;
  count: number;
}

/**
 * Get distinct countries with place counts, respecting active filters (except country).
 * Used for the country filter dropdown showing per-country counts.
 */
export async function getCountries(filter?: Omit<PlaceFilter, 'country'>): Promise<CountryOption[]> {
  const backendFilter: Record<string, unknown> = {};
  if (filter) {
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'search') {
          backendFilter['searchQuery'] = value;
        } else if (key === 'sortBy') {
          backendFilter['orderBy'] = value;
        } else if (key === 'sortOrder') {
          backendFilter['orderDir'] = value;
        } else if (key === 'priceLevel' && Array.isArray(value)) {
          backendFilter['priceLevel'] = value.join(',');
        } else if (key === 'excludeClickedByUserIds' && Array.isArray(value)) {
          backendFilter['excludeClickedByUserIds'] = value.join(',');
        } else if (key === 'excludePlaceIds' && Array.isArray(value)) {
          backendFilter['excludePlaceIds'] = value.join(',');
        } else {
          backendFilter[key] = value;
        }
      }
    });
  }
  const queryString = Object.keys(backendFilter).length > 0 ? buildQueryString(backendFilter) : '';
  const response = await get<{ countries: CountryOption[] }>(`/places/countries${queryString}`);
  return response.countries || [];
}

/**
 * Export places to file (server-side)
 */
export async function exportPlaces(
  jobId: string,
  format: ExportFormat,
  options?: {
    filter?: PlaceFilter;
    columns?: string[];
  }
): Promise<ExportStatusResponse> {
  return post<ExportStatusResponse>(`${ENDPOINTS.JOB_EXPORT(jobId)}`, {
    format,
    filter: options?.filter,
    columns: options?.columns,
  });
}

/**
 * Get export status
 */
export async function getExportStatus(exportId: string): Promise<ExportStatusResponse> {
  return get<ExportStatusResponse>(ENDPOINTS.EXPORT_STATUS(exportId));
}

/**
 * Get export download URL
 */
export function getExportDownloadUrl(jobId: string, format: ExportFormat): string {
  return `${ENDPOINTS.JOB_EXPORT(jobId)}?format=${format}&download=true`;
}

/**
 * Download export directly (for small datasets)
 */
export async function downloadExport(
  jobId: string,
  format: ExportFormat,
  filename?: string
): Promise<void> {
  const url = getExportDownloadUrl(jobId, format);

  // Create a temporary link and click it
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `export-${jobId}.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Get places near a location
 */
export async function getPlacesNearLocation(
  lat: number,
  lng: number,
  radiusKm: number = 1,
  limit: number = 50
): Promise<PlaceListItem[]> {
  const response = await get<PlacesResponse>(
    `${ENDPOINTS.PLACES}/nearby?lat=${lat}&lng=${lng}&radius=${radiusKm}&limit=${limit}`
  );
  return response.places;
}

/**
 * Get places by category
 */
export async function getPlacesByCategory(
  category: string,
  jobId?: string,
  limit: number = 50
): Promise<PlaceListItem[]> {
  const response = await getPlaces({
    category,
    jobId,
    limit,
  });
  return response.places;
}

/**
 * Get top rated places
 */
export async function getTopRatedPlaces(
  jobId?: string,
  limit: number = 10
): Promise<PlaceListItem[]> {
  const response = await getPlaces({
    jobId,
    minRating: 4.0,
    sortBy: 'rating',
    sortOrder: 'desc',
    limit,
  });
  return response.places;
}

/**
 * Get places with websites
 */
export async function getPlacesWithWebsites(
  jobId?: string,
  limit: number = 50
): Promise<PlaceListItem[]> {
  const response = await getPlaces({
    jobId,
    hasWebsite: true,
    limit,
  });
  return response.places;
}

/**
 * Get places with phone numbers
 */
export async function getPlacesWithPhones(
  jobId?: string,
  limit: number = 50
): Promise<PlaceListItem[]> {
  const response = await getPlaces({
    jobId,
    hasPhone: true,
    limit,
  });
  return response.places;
}

/**
 * Response from rescan endpoint
 */
export interface RescanResponse {
  success: boolean;
  place: Place | null;
  extractedFields: string[];
  elapsedMs: number;
  error?: string;
}

/**
 * Rescan a place to refresh its details
 * Opens a browser on the backend, navigates to the place's Google Maps URL,
 * and extracts fresh data.
 */
export async function rescanPlace(placeId: string | number): Promise<RescanResponse> {
  return post<RescanResponse>(`/places/${placeId}/rescan`, {});
}
